// =====================================================
// COMPONENTE DE INPUT COM MÁSCARA DE MOEDA V2 - ROBUSTO
// =====================================================

import React, { useState, useEffect, useRef } from 'react'

interface CurrencyInputV2Props {
    value: string | number
    onChange: (value: string) => void
    placeholder?: string
    className?: string
    disabled?: boolean
    required?: boolean
    name?: string
    id?: string
}

export function CurrencyInputV2({
    value,
    onChange,
    placeholder = "R$ 0,00",
    className = "",
    disabled = false,
    required = false,
    name,
    id
}: CurrencyInputV2Props) {
    const [displayValue, setDisplayValue] = useState('')
    const [isFocused, setIsFocused] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const cursorPositionRef = useRef<number>(0)

    // Função para formatar valor para exibição
    const formatForDisplay = (numericValue: number): string => {
        if (numericValue === 0) return ''

        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(numericValue)
    }

    // Função para extrair apenas números
    const extractNumbers = (value: string): string => {
        return value.replace(/\D/g, '')
    }

    // Função para converter string de números em valor decimal
    const numbersToDecimal = (numbers: string): number => {
        if (numbers.length === 0) return 0
        return parseInt(numbers) / 100
    }

    // Função removida - não utilizada no momento

    // Atualiza o valor de exibição quando o valor prop muda
    useEffect(() => {
        if (value && value !== '') {
            const numericValue = typeof value === 'string' ? parseFloat(value) : value
            if (!isNaN(numericValue) && numericValue > 0) {
                setDisplayValue(formatForDisplay(numericValue))
            } else {
                setDisplayValue('')
            }
        } else {
            setDisplayValue('')
        }
    }, [value])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value
        const cursorPosition = e.target.selectionStart || 0

        // Salva a posição do cursor
        cursorPositionRef.current = cursorPosition

        // Extrai apenas os números
        const numbers = extractNumbers(inputValue)

        // Converte para decimal
        const decimalValue = numbersToDecimal(numbers)

        // Formata para exibição
        const formattedValue = formatForDisplay(decimalValue)

        setDisplayValue(formattedValue)

        // Chama onChange com o valor decimal
        onChange(decimalValue.toString())
    }

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true)
        // Seleciona todo o texto quando foca no input
        setTimeout(() => {
            e.target.select()
        }, 0)
    }

    const handleBlur = () => {
        setIsFocused(false)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Permite teclas de controle
        const allowedKeys = [
            'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
            'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
            'Home', 'End'
        ]

        if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
            return
        }

        // Permite apenas números
        if (!/[0-9]/.test(e.key)) {
            e.preventDefault()
        }
    }

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault()

        const pastedText = e.clipboardData.getData('text')
        const numbers = extractNumbers(pastedText)

        if (numbers.length > 0) {
            const decimalValue = numbersToDecimal(numbers)
            const formattedValue = formatForDisplay(decimalValue)

            setDisplayValue(formattedValue)
            onChange(decimalValue.toString())
        }
    }

    return (
        <input
            ref={inputRef}
            type="text"
            value={displayValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={isFocused ? "Digite apenas números" : placeholder}
            className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 ${className}`}
            disabled={disabled}
            required={required}
            name={name}
            id={id}
            autoComplete="off"
        />
    )
}
